import React, { useCallback, useState } from 'react';

import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { UtilService } from 'app/services/util.service';
import { isValidWord } from 'app/validators/validateWord';

function removeMultipleWhiteSpaces(value: string) {
  return value.replace(/ +(?= )/g, '');
}

export function useTextControl(
  props: WrappedControlProps,
  options: {
    charValidation?: (value: string) => boolean;
    valueNormalize?: (value: string) => string;
  }
) {
  const {
    input,
    meta: { touched, valid },
    onValueChange,
    onControlBlur,
    onClick,
  } = props;

  const { charValidation = isValidWord, valueNormalize } = options;

  const [focused, setFocused] = useState(false);
  const onFocus = useCallback(() => setFocused(true), []);
  const error = touched && !valid;
  const isValueCheckVisible = !focused && UtilService.hasValue(input.value);

  const normalizeValue = (value: string) => {
    let normalizedValue = removeMultipleWhiteSpaces(value);

    normalizedValue = valueNormalize
      ? valueNormalize(normalizedValue)
      : normalizedValue;

    return normalizedValue;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const isValid = charValidation(value);

    if (value !== '' && !isValid) {
      return;
    }

    const normalizedValue = normalizeValue(value);

    input.onChange(normalizedValue);

    if (onValueChange) {
      onValueChange(normalizedValue, false, false);
    }
  };

  const handleBlur = () => {
    const { value } = input;
    const trimmedValue = value.trim();
    const shouldPatch = charValidation(trimmedValue);

    setFocused(false);
    input.onBlur(trimmedValue);

    if (onControlBlur) {
      onControlBlur(shouldPatch);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return {
    error,
    focused,
    handleBlur,
    handleChange,
    handleClick,
    isValueCheckVisible,
    onFocus,
  };
}
